import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, User, Mail, Phone, Cake, MapPin, Calendar, Save, FileText, Box, MessageSquare, Upload, Download, Trash2, File as FileIcon, FileSpreadsheet, FileImage, FileType2 } from "lucide-react";

const STATUS_COLOR = {
  confirmed: "bg-sage/15 text-sage",
  pending: "bg-terracotta/15 text-terracotta",
  cancelled: "bg-ink/10 text-ink-muted",
  completed: "bg-ink/10 text-ink",
};

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState({ behandlung: "", material: "", bemerkung: "" });

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/customers/${id}`).then((r) => {
      setData(r.data);
      setNotes(r.data.notes || { behandlung: "", material: "", bemerkung: "" });
    }).catch(() => toast.error("Kundin nicht gefunden")).finally(() => setLoading(false));
  }, [id]);

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/customers/${id}/notes`, notes);
      toast.success("Notizen gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-ink-muted">Lädt…</div>;
  if (!data) return <div className="p-8 text-ink-muted">Kundin nicht gefunden.</div>;

  const c = data.customer;
  const bookings = data.bookings || [];
  const totalSpent = bookings.filter((b) => b.status !== "cancelled").reduce((sum, b) => sum + (Number(b.service_price) || 0), 0);

  return (
    <div className="p-5 sm:p-8 lg:p-10 max-w-5xl" data-testid="customer-detail-page">
      <Link to="/admin/customers" data-testid="back-to-customers" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-terracotta mb-5">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Zurück zur Liste
      </Link>

      <h1 className="font-display text-3xl sm:text-4xl text-ink flex items-center gap-3">
        <User className="w-7 h-7 text-terracotta" strokeWidth={1.5} /> {c.name}
      </h1>

      {/* Personal info card */}
      <div className="mt-8 bg-white border border-border rounded-md p-5 sm:p-6 grid sm:grid-cols-2 gap-4">
        <InfoRow icon={Mail} label="E-Mail" value={c.email} link={`mailto:${c.email}`} />
        <InfoRow icon={Phone} label="Telefon" value={c.phone} link={c.phone ? `tel:${c.phone}` : null} />
        <InfoRow icon={Cake} label="Geburtsdatum" value={c.birthdate || "—"} />
        <InfoRow icon={MapPin} label="Adresse" value={[c.address, [c.postal_code, c.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"} />
        <InfoRow icon={Calendar} label="Buchungen" value={`${bookings.length} (CHF ${Math.round(totalSpent)} gesamt)`} />
        <InfoRow icon={Calendar} label="Letzter Termin" value={c.last_booking_at ? c.last_booking_at.slice(0, 10) : "—"} />
      </div>

      {/* Notes – 3 fields */}
      <div className="mt-8">
        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
          <h2 className="font-display text-2xl text-ink">Notizen zur Kundin</h2>
          <button onClick={saveNotes} disabled={saving} data-testid="customer-notes-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-terracotta text-white rounded-full hover:bg-terracotta-hover disabled:opacity-40">
            <Save className="w-4 h-4" strokeWidth={1.5} />
            {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
        <div className="grid lg:grid-cols-3 gap-4">
          <NoteField icon={FileText} label="Behandlung" value={notes.behandlung} onChange={(v) => setNotes((n) => ({ ...n, behandlung: v }))} placeholder="z.B. Abhyanga mit Sesamöl, Schwerpunkt Rücken…" testid="note-behandlung" />
          <NoteField icon={Box} label="Benutztes Material" value={notes.material} onChange={(v) => setNotes((n) => ({ ...n, material: v }))} placeholder="z.B. Sesamöl, Kräutermischung Vata, warme Tücher…" testid="note-material" />
          <NoteField icon={MessageSquare} label="Bemerkung" value={notes.bemerkung} onChange={(v) => setNotes((n) => ({ ...n, bemerkung: v }))} placeholder="z.B. Allergie gegen Lavendel, bevorzugt sanften Druck…" testid="note-bemerkung" />
        </div>
      </div>

      {/* Documents library (Word, Excel, PDF, Bilder) */}
      <CustomerDocuments customerId={id} />

      {/* Bookings history */}
      <div className="mt-10">
        <h2 className="font-display text-2xl text-ink mb-4">Buchungs-Historie</h2>
        {bookings.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-md p-8 text-center text-ink-muted">Noch keine Buchungen</div>
        ) : (
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-3">Datum</th>
                  <th className="text-left px-4 py-3">Zeit</th>
                  <th className="text-left px-4 py-3">Behandlung</th>
                  <th className="text-left px-4 py-3">Standort</th>
                  <th className="text-left px-4 py-3">Preis</th>
                  <th className="text-left px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody data-testid="customer-bookings-table">
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-border">
                    <td className="px-4 py-3 text-ink">{b.date}</td>
                    <td className="px-4 py-3 text-ink-muted">{b.time}</td>
                    <td className="px-4 py-3 text-ink">{b.service_name_snapshot}<div className="text-xs text-ink-muted">{b.service_duration} Min</div></td>
                    <td className="px-4 py-3 text-ink-muted text-xs">{b.location === "rupperswil" ? "Rupperswil" : "Sarmenstorf"}</td>
                    <td className="px-4 py-3 text-ink">CHF {b.service_price}</td>
                    <td className="px-4 py-3"><span className={`inline-block px-2 py-0.5 rounded-full text-xs ${STATUS_COLOR[b.status] || ""}`}>{b.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const InfoRow = ({ icon: Icon, label, value, link }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 mt-1 text-terracotta shrink-0" strokeWidth={1.5} />
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-widest text-ink-muted">{label}</div>
      {link ? (
        <a href={link} className="text-sm text-ink hover:text-terracotta break-all">{value}</a>
      ) : (
        <div className="text-sm text-ink break-all">{value}</div>
      )}
    </div>
  </div>
);

const NoteField = ({ icon: Icon, label, value, onChange, placeholder, testid }) => (
  <label className="block bg-white border border-border rounded-md p-4">
    <span className="text-xs uppercase tracking-widest text-ink-muted flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 text-terracotta" strokeWidth={1.5} /> {label}
    </span>
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={6}
      placeholder={placeholder}
      className="mt-2 w-full px-3 py-2.5 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-terracotta/30 resize-y"
    />
  </label>
);

// ---------- Customer Documents ----------
const ALLOWED_EXT = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt", ".jpg", ".jpeg", ".png", ".webp"];
const MAX_MB = 25;

function iconFor(filename) {
  const n = (filename || "").toLowerCase();
  if (n.endsWith(".pdf")) return <FileType2 className="w-5 h-5 text-red-500" strokeWidth={1.5} />;
  if (n.endsWith(".doc") || n.endsWith(".docx")) return <FileText className="w-5 h-5 text-blue-600" strokeWidth={1.5} />;
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return <FileSpreadsheet className="w-5 h-5 text-green-600" strokeWidth={1.5} />;
  if (n.match(/\.(jpe?g|png|webp|gif)$/)) return <FileImage className="w-5 h-5 text-purple-500" strokeWidth={1.5} />;
  return <FileIcon className="w-5 h-5 text-ink-muted" strokeWidth={1.5} />;
}

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function CustomerDocuments({ customerId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/customers/${customerId}/documents`);
      setDocs(r.data || []);
    } catch (e) {
      toast.error("Dokumente konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const uploadFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    setUploading(true);
    let ok = 0;
    for (const file of arr) {
      const ext = "." + file.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) {
        toast.error(`${file.name}: Format nicht erlaubt`);
        continue;
      }
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`${file.name}: zu gross (max ${MAX_MB} MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        await api.post(`/admin/customers/${customerId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`${file.name}: Upload fehlgeschlagen`);
      }
    }
    if (ok > 0) toast.success(`${ok} Datei${ok > 1 ? "en" : ""} hochgeladen`);
    setUploading(false);
    await load();
  };

  const download = async (doc) => {
    try {
      // Use fetch with auth to get blob, then create download link so OS opens with default app
      const token = localStorage.getItem("admin_token");
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${backendUrl}/api/admin/customers/${customerId}/documents/${doc.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download fehlgeschlagen");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`"${doc.filename}" wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/customers/${customerId}/documents/${doc.id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  return (
    <div className="mt-10" data-testid="customer-documents-section">
      <h2 className="font-display text-2xl text-ink mb-4">Dokumente</h2>

      {/* Drag & drop / picker */}
      <label
        data-testid="doc-upload-dropzone"
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          uploadFiles(e.dataTransfer.files);
        }}
        className={`block cursor-pointer border-2 border-dashed rounded-md p-6 text-center transition-colors ${
          dragActive ? "border-terracotta bg-terracotta/5" : "border-border bg-white hover:border-terracotta/60"
        } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
      >
        <input
          type="file"
          multiple
          accept={ALLOWED_EXT.join(",")}
          className="hidden"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          data-testid="doc-upload-input"
          disabled={uploading}
        />
        <Upload className="w-6 h-6 text-terracotta mx-auto mb-2" strokeWidth={1.5} />
        <div className="text-sm text-ink">
          {uploading ? "Wird hochgeladen…" : (
            <>
              <span className="font-medium">Datei auswählen</span> oder hierhin ziehen
            </>
          )}
        </div>
        <div className="text-xs text-ink-muted mt-1">
          PDF · Word · Excel · JPG/PNG · max {MAX_MB} MB
        </div>
      </label>

      {/* List */}
      <div className="mt-4">
        {loading ? (
          <div className="text-sm text-ink-muted">Lädt…</div>
        ) : docs.length === 0 ? (
          <div className="bg-white border border-dashed border-border rounded-md p-6 text-center text-sm text-ink-muted">
            Noch keine Dokumente hochgeladen.
          </div>
        ) : (
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="text-left px-4 py-3">Datei</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Grösse</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Hochgeladen</th>
                  <th className="text-right px-4 py-3">Aktionen</th>
                </tr>
              </thead>
              <tbody data-testid="customer-documents-list">
                {docs.map((d) => (
                  <tr key={d.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => download(d)}
                        className="flex items-center gap-2.5 text-left text-ink hover:text-terracotta"
                        data-testid={`doc-open-${d.id}`}
                        title="Herunterladen und mit Programm öffnen"
                      >
                        {iconFor(d.filename)}
                        <span className="truncate max-w-[280px] sm:max-w-[420px]">{d.filename}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-ink-muted hidden sm:table-cell">{humanSize(d.size)}</td>
                    <td className="px-4 py-3 text-ink-muted hidden md:table-cell text-xs">{(d.uploaded_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => download(d)}
                          className="p-2 text-ink-muted hover:text-terracotta rounded"
                          data-testid={`doc-download-${d.id}`}
                          title="Herunterladen"
                        >
                          <Download className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => remove(d)}
                          className="p-2 text-ink-muted hover:text-red-600 rounded"
                          data-testid={`doc-delete-${d.id}`}
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
